import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";

type ApacheSettings = {
  memorySoftLimit: number;
  memoryHardLimit: number;
  processSoftLimit: number;
  processHardLimit: number;
  apacheCpuSoftLimit: number;
  apacheCpuHardLimit: number;
  directoryListing: "On" | "Off";
  keepAlive: "On" | "Off";
  followSymLinks: "On" | "Off";
  serverSignature: "On" | "Off";
  serverTokens: string;
  traceEnable: "On" | "Off";
  accessFileName: string;
  allowOverride: string;
  keepAliveTimeout: number;
  maxKeepAliveRequests: number;
  timeout: number;
  startServers: number;
  minSpareServers: number;
  maxSpareServers: number;
  maxRequestWorkers: number;
  maxConnectionsPerChild: number;
  enableSendfile: "On" | "Off";
  enableMmap: "On" | "Off";
  enableHttp2: boolean;
  enableGzip: boolean;
  enableUserModDir: boolean;
  noIpInVirtualHost: boolean;
  disableModRuid2: boolean;
  additionalServerAliases: string;
  sslUseStapling: "On" | "Off";
  extendedStatus: "On" | "Off";
  logLevel: string;
  mpm: "prefork" | "event" | "worker";
  enableFastCgi: boolean;
};

function Toggle({
  label,
  value,
  onChange,
  hint
}: {
  label: string;
  value: "On" | "Off";
  onChange: (v: "On" | "Off") => void;
  hint?: string;
}) {
  return (
    <div className="apache-field">
      <div className="apache-field-label">
        <strong>{label}</strong>
        {hint ? <div className="apache-field-hint">{hint}</div> : null}
      </div>
      <div className="apache-toggle">
        <label>
          <input
            type="radio"
            checked={value === "On"}
            onChange={() => onChange("On")}
          />{" "}
          On
        </label>
        <label>
          <input
            type="radio"
            checked={value === "Off"}
            onChange={() => onChange("Off")}
          />{" "}
          Off
        </label>
      </div>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  hint,
  unit
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  hint?: string;
  unit?: string;
}) {
  return (
    <div className="apache-field">
      <div className="apache-field-label">
        <strong>{label}</strong>
        {hint ? <div className="apache-field-hint">{hint}</div> : null}
      </div>
      <div className="apache-field-control">
        <input
          type="number"
          className="mono"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        {unit ? <span className="apache-unit">{unit}</span> : null}
      </div>
    </div>
  );
}

/**
 * Softaculous/Webuzo-style Apache Settings form.
 */
export function ApacheSettingsPage() {
  const [s, setS] = useState<ApacheSettings | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api<{ settings: ApacheSettings }>(
        "/api/v1/server-config/apache-settings"
      );
      setS(res.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof ApacheSettings>(key: K, val: ApacheSettings[K]) => {
    setS((prev) => (prev ? { ...prev, [key]: val } : prev));
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!s) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{ settings: ApacheSettings }>(
        "/api/v1/server-config/apache-settings",
        {
          method: "PUT",
          body: JSON.stringify(s)
        }
      );
      setS(res.settings);
      const apply = await api<{ result?: { detail?: string } }>(
        "/api/v1/server-config/web-apply",
        {
          method: "POST",
          body: JSON.stringify({})
        }
      ).catch(() => null);
      setMsg(
        apply?.result?.detail
          ? `Saved · ${apply.result.detail}`
          : "Apache settings saved"
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!s) {
    return (
      <div className="card full">
        <p className="card-meta">{error || "Loading Apache settings…"}</p>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Apache Settings</h1>
          <p>
            Web server MPM, modules, SSL, workers, and limits (Webuzo-style)
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <form onSubmit={(e) => void save(e)}>
        {/* MPM / FastCGI */}
        <section className="apache-card">
          <h2 className="apache-card-title">Apache Settings</h2>
          <div className="apache-field">
            <div className="apache-field-label">
              <strong>Enable Fast CGI</strong>
              <div className="apache-field-hint">
                Required for MultiPHP / PHP-FPM (recommended)
              </div>
            </div>
            <label className="apache-check">
              <input
                type="checkbox"
                checked={s.enableFastCgi}
                onChange={(e) => set("enableFastCgi", e.target.checked)}
              />
              Enabled
            </label>
          </div>
          <div className="apache-field">
            <div className="apache-field-label">
              <strong>MPM</strong>
            </div>
            <div className="apache-toggle apache-toggle-col">
              {(
                [
                  ["prefork", "MPM Prefork (Default)"],
                  ["event", "MPM Event"],
                  ["worker", "MPM Worker"]
                ] as const
              ).map(([v, label]) => (
                <label key={v}>
                  <input
                    type="radio"
                    checked={s.mpm === v}
                    onChange={() => set("mpm", v)}
                  />{" "}
                  {label}
                </label>
              ))}
            </div>
          </div>
          <p className="card-meta faint">
            Note: For MultiPHP, FastCGI should be enabled.
          </p>
        </section>

        {/* Additional modules */}
        <section className="apache-card">
          <h2 className="apache-card-title">Additional Modules Settings</h2>
          <div className="apache-grid-2">
            <label className="apache-check">
              <input
                type="checkbox"
                checked={s.enableHttp2}
                onChange={(e) => set("enableHttp2", e.target.checked)}
              />
              Enable HTTP/2 protocol
            </label>
            <label className="apache-check">
              <input
                type="checkbox"
                checked={s.enableGzip}
                onChange={(e) => set("enableGzip", e.target.checked)}
              />
              Enable Gzip Compression
            </label>
            <label className="apache-check">
              <input
                type="checkbox"
                checked={s.enableUserModDir}
                onChange={(e) => set("enableUserModDir", e.target.checked)}
              />
              Enable USER MOD DIR
            </label>
            <label className="apache-check">
              <input
                type="checkbox"
                checked={s.noIpInVirtualHost}
                onChange={(e) => set("noIpInVirtualHost", e.target.checked)}
              />
              No IP in Virtual Host
            </label>
            <label className="apache-check">
              <input
                type="checkbox"
                checked={s.disableModRuid2}
                onChange={(e) => set("disableModRuid2", e.target.checked)}
              />
              Disable Mod ruid2
            </label>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <label>Additional Server Aliases</label>
            <input
              value={s.additionalServerAliases}
              onChange={(e) => set("additionalServerAliases", e.target.value)}
              placeholder="optional aliases"
            />
          </div>
        </section>

        {/* Resource limits */}
        <section className="apache-card">
          <h2 className="apache-card-title">Resource Limits</h2>
          <NumField
            label="Memory Soft Limit"
            hint="Default: 262144"
            unit="KB"
            value={s.memorySoftLimit}
            onChange={(n) => set("memorySoftLimit", n)}
          />
          <NumField
            label="Memory Hard Limit"
            hint="Default: 262144"
            unit="KB"
            value={s.memoryHardLimit}
            onChange={(n) => set("memoryHardLimit", n)}
          />
          <NumField
            label="Process Soft Limit"
            hint="Default: 400"
            value={s.processSoftLimit}
            onChange={(n) => set("processSoftLimit", n)}
          />
          <NumField
            label="Process Hard Limit"
            hint="Default: 500"
            value={s.processHardLimit}
            onChange={(n) => set("processHardLimit", n)}
          />
          <NumField
            label="Apache CPU Soft Limit"
            hint="Default: 80"
            unit="%"
            value={s.apacheCpuSoftLimit}
            onChange={(n) => set("apacheCpuSoftLimit", n)}
          />
          <NumField
            label="Apache CPU Hard Limit"
            hint="Default: 100"
            unit="%"
            value={s.apacheCpuHardLimit}
            onChange={(n) => set("apacheCpuHardLimit", n)}
          />
        </section>

        {/* Global configuration */}
        <section className="apache-card">
          <h2 className="apache-card-title">Global Configuration</h2>
          <Toggle
            label="Directory Listing"
            hint="System Default : Off"
            value={s.directoryListing}
            onChange={(v) => set("directoryListing", v)}
          />
          <Toggle
            label="Keep-Alive"
            hint="System Default : On"
            value={s.keepAlive}
            onChange={(v) => set("keepAlive", v)}
          />
          <Toggle
            label="FollowSymLinks"
            hint="System Default : Off · PCI Recommendation : Off"
            value={s.followSymLinks}
            onChange={(v) => set("followSymLinks", v)}
          />
          <Toggle
            label="Server Signature"
            hint="System Default : On"
            value={s.serverSignature}
            onChange={(v) => set("serverSignature", v)}
          />
          <div className="apache-field">
            <div className="apache-field-label">
              <strong>Server Tokens</strong>
              <div className="apache-field-hint">System Default : Full</div>
            </div>
            <select
              value={s.serverTokens}
              onChange={(e) => set("serverTokens", e.target.value)}
            >
              {["Full", "OS", "Minimal", "Minor", "Major", "Prod"].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <Toggle
            label="Trace Enable"
            hint="System Default : Off · PCI Recommendation : Off"
            value={s.traceEnable}
            onChange={(v) => set("traceEnable", v)}
          />
          <div className="apache-field">
            <div className="apache-field-label">
              <strong>Access File Name</strong>
              <div className="apache-field-hint">System Default : .htaccess</div>
            </div>
            <input
              className="mono"
              value={s.accessFileName}
              onChange={(e) => set("accessFileName", e.target.value)}
            />
          </div>
          <div className="apache-field">
            <div className="apache-field-label">
              <strong>Allow Override</strong>
              <div className="apache-field-hint">System Default : All</div>
            </div>
            <input
              className="mono"
              value={s.allowOverride}
              onChange={(e) => set("allowOverride", e.target.value)}
            />
          </div>
          <Toggle
            label="SSL Use Stapling"
            value={s.sslUseStapling}
            onChange={(v) => set("sslUseStapling", v)}
          />
          <Toggle
            label="Extended Status"
            value={s.extendedStatus}
            onChange={(v) => set("extendedStatus", v)}
          />
          <div className="apache-field">
            <div className="apache-field-label">
              <strong>LogLevel</strong>
              <div className="apache-field-hint">System Default : warn</div>
            </div>
            <select
              value={s.logLevel}
              onChange={(e) => set("logLevel", e.target.value)}
            >
              {["emerg", "alert", "crit", "error", "warn", "notice", "info", "debug"].map(
                (l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                )
              )}
            </select>
          </div>
        </section>

        {/* Workers / performance */}
        <section className="apache-card">
          <h2 className="apache-card-title">Workers &amp; Performance</h2>
          <NumField
            label="Keep-Alive Timeout"
            hint="Default: 5"
            unit="sec"
            value={s.keepAliveTimeout}
            onChange={(n) => set("keepAliveTimeout", n)}
          />
          <NumField
            label="Max Keep-Alive Requests"
            hint="Default: 100"
            value={s.maxKeepAliveRequests}
            onChange={(n) => set("maxKeepAliveRequests", n)}
          />
          <NumField
            label="Timeout"
            hint="Default: 300"
            unit="sec"
            value={s.timeout}
            onChange={(n) => set("timeout", n)}
          />
          <NumField
            label="Start Servers"
            hint="Default: 5"
            value={s.startServers}
            onChange={(n) => set("startServers", n)}
          />
          <NumField
            label="Minimum Spare Servers"
            hint="Default: 5"
            value={s.minSpareServers}
            onChange={(n) => set("minSpareServers", n)}
          />
          <NumField
            label="Maximum Spare Servers"
            hint="Default: 10"
            value={s.maxSpareServers}
            onChange={(n) => set("maxSpareServers", n)}
          />
          <NumField
            label="Max Request Workers"
            hint="Default: 150"
            value={s.maxRequestWorkers}
            onChange={(n) => set("maxRequestWorkers", n)}
          />
          <NumField
            label="Max Connections Per Child"
            hint="Default: 10000"
            value={s.maxConnectionsPerChild}
            onChange={(n) => set("maxConnectionsPerChild", n)}
          />
          <Toggle
            label="Enable Sendfile"
            hint="Default: On"
            value={s.enableSendfile}
            onChange={(v) => set("enableSendfile", v)}
          />
          <Toggle
            label="Enable MMAP"
            hint="Default: On"
            value={s.enableMmap}
            onChange={(v) => set("enableMmap", v)}
          />
        </section>

        <div style={{ marginTop: 16, marginBottom: 32 }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy}
          >
            {busy ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </form>
    </>
  );
}
